import { RSAA } from 'redux-api-middleware'
import * as R from 'ramda'

/**
 * This middleware adds a handler to the meta of all ERROR actions. This handler
 * is responsible for reading the response headers and then appending them to
 * the action's meta object. This meta object is read by the reportErrors
 * middleware so that the trace-id can be logged and included with other
 * debugging information.
 */
export default () => next => action => {
  if (!action[RSAA]) return next(action)

  const newAction = { ...action }
  newAction[RSAA].types = newAction[RSAA].types.map(type => {
    // mostly the type is just a string but it might also sometimes
    // already be an object with meta or even another payload.
    const isString = typeof type === 'string'
    const actionType = isString ? type : type?.type

    // if the action isn't one that needs to have a traceId we can just bail
    if (!/ERROR$/.test(actionType)) return type

    // this type is now always going to be an object, and we're going to
    // add our meta handler there. Be sure to send it any existing meta
    // data so that it can be included in the final result.
    const newType = R.ifElse(
      R.is(String),
      type => ({
        type,
        meta: createMetaHandler(),
      }),
      ({ meta, ...other }) => ({
        ...other,
        meta: createMetaHandler(meta),
      }),
    )(type)

    return newType
  })

  return next(newAction)
}

// helper function to create a handler for getting headers from
// from the response and ultimately the trace-id, so that we can
// report it along with the error. Headers should be joined up
// with any existing meta.
function createMetaHandler(meta) {
  if (typeof meta === 'function') {
    // Pass the args down if meta is a function
    return (action, state, res) => ({
      ...meta(action, state, res),
      traceId: res.headers.get('trace-id'),
    })
  } else if (typeof meta === 'string') {
    // Label meta as meta if it's a string
    return (_, __, res) => ({
      meta,
      traceId: res.headers.get('trace-id'),
    })
  } else {
    // Spread the meta if it's `undefined` `null` `object` or anything else
    return (_, __, res) => ({
      ...meta,
      traceId: res.headers.get('trace-id'),
    })
  }
}
